export enum ItemTypeEnum {
  Hotel = 'Hotel',
  Cruise = 'Cruise',
  Condo = 'Condo',
}

export enum CertificateStatusEnum {
  New = 'New',
  Pending = 'Pending',
  Active = 'Active',
  PartiallyRedeemed = 'PartiallyRedeemed',
  Redeemed = 'Redeemed',
  Expired = 'Expired',
  Canceled = 'Canceled',
}

export interface ICertificateItemBase {
  readonly id: string;
  type: ItemTypeEnum;
  name: string;
  nights: number;
  internalPrice: number;
  publicPrice: number;
  readonly createdAt: string;
  readonly updatedAt?: string;
}

export interface ICertificateItem extends ICertificateItemBase {
  readonly certificateId: string;
  status: CertificateStatusEnum;
}

export interface ICertificateItemTemplate extends ICertificateItemBase {
  readonly certificateTemplateId: string;
  description: string;
}

export interface ICertificateEntityBase {
  readonly id: string;
  name: string;
  internalNotes: string;
  validityMonths: number;
  internalPrice: number;
  marginPrice: number;
  url: string;
  readonly createdAt: string;
  readonly updatedAt?: string;
}

export interface ICertificateTemplate extends ICertificateEntityBase {
  code: string;
  isLive: boolean;
  totalPrice: number;
  itemTemplates: ICertificateItemTemplate[];
  selectedClubSiteIds: string[];
  allowAllClubSites: boolean;
  averageRetailPrice: number;
  savings: number;
  imageUrl: string | null;
  descriptionContent: string | null;
  eligibleCruiseLines?: ICertificateCruiseLine[];
  termsAndConditionsContent?: string;
  landingPageId?: string | null;
  landingPageSlug?: string | null;
}

export interface ICertificateTermsTemplate {
  readonly id: string;
  name: string;
  content: string;
  isDefault: boolean;
  referenceCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ICertificateTemplatesParams {
  clubSiteIds: number[];
  search?: string;
  pageNumber: number | null | undefined;
  pageSize: number | null | undefined;
  orderBy?: string;
  sortOrder?: string;
  itemTypes?: ItemTypeEnum[];
}

export interface ICertificateTemplatesResponse {
  items: ICertificateTemplate[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export enum CertificateChannelEnum {
  ClubSite = 'ClubSite',
  Manual = 'Manual',
}

/** Values returned by the certificates API for `paymentType` (wallet / details). */
export const CertificateWalletPaymentType = {
  Instalments: 'Instalments',
  InFull: 'InFull',
  NotPaid: 'NotPaid',
  InFullSplitTwoCards: 'InFullSplitTwoCards',
  InstalmentsSplitDownPaymentTwoCards: 'InstalmentsSplitDownPaymentTwoCards',
} as const;

export type CertificateWalletPaymentType =
  (typeof CertificateWalletPaymentType)[keyof typeof CertificateWalletPaymentType];

export function isInstalmentWalletPaymentType(paymentType: string): boolean {
  return (
    paymentType === CertificateWalletPaymentType.Instalments ||
    paymentType === CertificateWalletPaymentType.InstalmentsSplitDownPaymentTwoCards
  );
}

/** Per-instalment row from the wallet/details API (`instalmentSchedule.instalments`). */
export type InstalmentScheduleItemStatus = 'scheduled' | 'paid' | 'failed';

export interface IInstalmentScheduleEntry {
  number: number;
  amount: number;
  status: InstalmentScheduleItemStatus;
  /** ISO due date for this instalment. */
  date: string;
}

/** Instalment summary + line items returned on user certificates (wallet). */
export interface IInstalmentSchedule {
  totalInstalments: number;
  instalmentsPaid: number;
  remainingBalance: number;
  instalments: readonly IInstalmentScheduleEntry[];
}

export interface IUserCertificate extends ICertificateEntityBase {
  code: string;
  items: ICertificateItem[];
  userId: string;
  recipientEmail: string;
  recipientName: string;
  channel: CertificateChannelEnum;
  clubSiteId: string;
  paymentType: string;
  paymentTypeDisplay: string;
  templateName: string;
  templateId: string;
  totalPrice: number;
  status: CertificateStatusEnum;
  expiresAt: string;
  purchasedAt: string;
  imageUrl: string | null;
  descriptionContent: string | null;
  eligibleCruiseLines?: ICertificateCruiseLine[];
  termsAndConditionsContent?: string;
  instalmentSchedule?: IInstalmentSchedule;
}

/** Certificate by id (wallet/detail); includes instalment schedule when applicable. */
export interface IUserCertificateWithInstallments extends IUserCertificate {
  instalmentSchedule?: IInstalmentSchedule;
}

export interface IUserCertificatesResponse {
  items: IUserCertificate[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/** Line item on a purchased certificate returned by GET certificate details. */
export interface ICertificateDetailsItem {
  readonly id: string;
  productType: ItemTypeEnum;
  nights: number;
  redemptionStatus: string;
}

/** Purchased certificate payload from GET `/certificates/{id}/details`. */
export interface ICertificateDetails {
  readonly id: string;
  name: string;
  certificateCode: string;
  status: CertificateStatusEnum;
  purchasedAt: string;
  expiresAt: string;
  totalPrice: number;
  savings: number;
  updatedAt: string;
  paymentType: string;
  templateImage: string;
  description: string;
  termsAndConditions: string;
  eligibleCruiseLines?: ICertificateCruiseLine[];
  certificateItems?: ICertificateDetailsItem[];
}

/** Sent on hotel/condo API requests instead of a bare certificateId. */
export interface ICertificateContext {
  certificateId: string;
  certificateItemId?: string;
}

export interface ICertificatePurchaseResult {
  httpStatusCode: number;
  purchaseId: string;
  certificateId: string;
  paymentId: string;
  transactionCode: string | null;
  paymentIntentId: string;
  chargeId: string;
  status: string;
  providerStatus: string;
  requires3DS: boolean;
  clientSecret: string;
  nextActionType: string;
  amount: number;
  currency: string;
  paymentLegs?: ICertificatePaymentLeg[];
}
export interface ICertificatePaymentLeg {
  id: string;
  paymentIntentId: number;
  status: string;
  currency: string;
  requires3DS: boolean;
  clientSecret: string;
  legIndex: number;
}

export interface ICertificatePurchaseRequestMetadata {
  source: 'web';
}

export enum CertificatePaymentModeEnum {
  PayInFull = 1,
  PayInFullSplit = 2,
  Instalments = 3,
  InstalmentsSplitDownPayment = 4,
}

export interface ICertificateSplitPaymentItem {
  paymentMethodId: string;
  amount: number;
}

export interface ICertificatePurchaseRequest {
  templateId: string;
  paymentMethodId: string;
  returnUrl: string;
  recipientName: string;
  recipientEmail: string;
  name: string;
  internalNotes: string;
  validityMonths: number;
  currency: 'USD';
  metadata: ICertificatePurchaseRequestMetadata;
  clubSiteId: number;
  paymentMode: CertificatePaymentModeEnum;
  primaryPaymentMethodId?: string;
  instalmentMonths?: number;
  downPaymentAmount?: number;
  splitPayments?: ICertificateSplitPaymentItem[];
  // HIDE FOR NOW before agreed with client side
  // title: string;
  // phone: string;
}

export interface IComplete3DSRequest {
  purchaseId: string;
  legIndex?: number;
}

/** Line item on GET /certificates/api/v1/payment-links/{token} wire response. */
export interface IPublicCertificatePaymentLinkItem {
  type: ItemTypeEnum;
  nights: number;
}

/**
 * Wire shape from GET /certificates/api/v1/payment-links/{token}.
 * Only present on successful (2xx) responses — invalid/expired tokens return Problem Details without this shape.
 */
export interface IPublicCertificatePaymentLinkResponse {
  certificateId: string;
  recipientName: string;
  recipientEmail: string;
  amount: number;
  certificateName: string;
  clubSiteId: number;
  expiresAt: string;
  imageUrl: string | null;
  validityMonths: number;
  items: readonly IPublicCertificatePaymentLinkItem[];
}

/**
 * UI-facing payment page context (mapped from {@link IPublicCertificatePaymentLinkResponse}).
 * Phase 6 will move mapping to @share/utils and consume wire type in UI directly.
 */
export interface IPublicCertificatePaymentContext {
  readonly id: string;
  name: string;
  totalPrice: number;
  recipientFirstName: string;
  recipientLastName: string;
  recipientEmail: string;
  items: readonly ICertificateItem[];
  imageUrl: string | null;
  descriptionContent: string | null;
  eligibleCruiseLines?: ICertificateCruiseLine[];
  termsAndConditionsContent?: string;
  currency: string;
  clubSiteId?: string | number;
  validityMonths: number;
  expiresAt?: string;
}

/** Public payment submit body — certificate resolved from URL token. */
export interface IPublicCertificatePaymentRequest {
  paymentMethodId: string;
  returnUrl: string;
  currency: 'USD';
  metadata: ICertificatePurchaseRequestMetadata;
  paymentMode: CertificatePaymentModeEnum;
  primaryPaymentMethodId?: string;
  instalmentMonths?: number;
  downPaymentAmount?: number;
  splitPayments?: ICertificateSplitPaymentItem[];
}

export interface ICertificatePaymentSuccessState {
  purchaseResult: ICertificatePurchaseResult;
  certificateTemplate: ICertificateTemplate;
  paymentMode?: CertificatePaymentModeEnum;
  /** Months in the instalment plan (4, 6, or 13); set when purchase completes on an instalment mode. */
  instalmentPlanMonths?: number;
  /** Holder first name for public payment success thank-you copy (no login session). */
  recipientFirstName?: string;
}

export interface ICertificatePaymentMethodUpdateRequest {
  paymentMethodId: string;
  returnUrl: string;
}

export interface ICertificatePaymentMethodUpdateResult {
  paymentMethodUpdatedAtUtc: string;
  retryScheduledForStepIds: string[];
  requiresAction: boolean;
  clientSecret: string | null;
  setupIntentId: string | null;
}

export interface ICertificatePaymentMethodConfirm3DSRequest {
  setupIntentId: string;
}

export interface ICertificateCruiseLine {
  id: string;
  cruiseLineId?: number;
  name: string;
  logoUrl?: string;
  videoUrl?: string;
}

export interface ErrorResponse {
  code: string;
  detail: string;
  reasonCode: string;
  requestId: string;
  spanId: string;
  status: number;
  title: string;
  traceId: string;
  type: string;
}
